
import { createApi } from '@stilingue/st-auth-utils';
import Service from '@/services/Service';
import { error401Handler } from '@/utils/error';

const gateway = process.env.VUE_APP_SERVICES_PROXY_URL || '';

const afterParser = response => response.data;

export class BlipService extends Service {
  constructor(gateway) {
    const api = createApi('/blip-service/', gateway, {
      authNotFound: () => true,
      error401Handler,
    });

    super(api, {
      afterParser,
      retriesLimit: 10,
    });

    this.api.http.defaults.timeout = 600000;
  }

  getReceivedMessages(params = {}) {
    return this.find({
      url: 'analytics/received-messages',
      ...params,
      crossDomain: true,
    });
  }

  getSentMessages(params = {}) {
    return this.find({
      url: 'analytics/sent-messages',
      ...params,
      crossDomain: true,
    });
  }

  getActiveMessages(params = {}) {
    return this.find({
      url: 'analytics/active-messages',
      ...params,
      crossDomain: true,
    });
  }

  getActiveIdentity(params = {}) {
    return this.find({
      url: 'analytics/active-identity',
      ...params,
      crossDomain: true,
    });
  }

  getEngagedIdentity(params = {}) {
    return this.find({
      url: 'analytics/engaged-identity',
      ...params,
      crossDomain: true,
    });
  }

  getEngagedUsers(params = {}) {
    return this.find({
      url: 'analytics/engaged-identity',
      ...params,
      crossDomain: true,
    });
  }

  getActiveMessagesPerDomain(params = {}) {
    return this.find({
      url: 'analytics/active-messages-per-domain',
      ...params,
      crossDomain: true,
    });
  }

  async getReceivedAndSentMessages(params = {}) {
    try {
      const receivedAndSentResponse = await Promise.all([
        this.getReceivedMessages(params),
        this.getSentMessages(params),
      ]);

      return receivedAndSentResponse;
    } catch (error) {
      console.error(error);
    }
  }

  getMetricsContacts(params = {}) {
    return this.find({
      url: 'analytics/contacts',
      ...params,
      crossDomain: true,
    });
  }

  getEventTrack(params = {}) {
    return this.find({
      url: 'analytics/event-track',
      ...params,
      crossDomain: true,
    });
  }

  getEventTrackCount(params = {}) {
    return this.find({
      url: 'analytics/event-track-count',
      ...params,
      crossDomain: true,
    });
  }

  getAudienceSummary(params = {}) {
    return this.find({
      url: 'activecampaign/audience-summary',
      ...params,
      crossDomain: true,
    });
  }

  getEventCategories(params = {}) {
    return this.find({
      url: 'analytics/event-track-categories',
      ...params,
      crossDomain: true,
    });
  }

  getDeskAgents(params = {}) {
    return this.find({
      url: 'desk/agents',
      ...params,
      crossDomain: true,
    });
  }

  getDeskTeams(params = {}) {
    return this.find({
      url: 'desk/teams',
      ...params,
      crossDomain: true,
    });
  }

  getDeskActiveTags(params = {}) {
    return this.find({
      url: 'desk/tags/active',
      ...params,
      crossDomain: true,
    });
  }

  getReportsTickets(params = {}) {
    return this.find({
      url: 'desk/analytics/reports/tickets',
      ...params,
      crossDomain: true,
    });
  }

  getReportsAttendantsProductivity(params = {}) {
    return this.find({
      url: 'desk/analytics/reports/attendants/productivity',
      ...params,
      crossDomain: true,
    });
  }

  getReportsTimings(params = {}) {
    return this.find({
      url: 'desk/analytics/reports/timings',
      ...params,
      crossDomain: true,
    });
  }

  getReportsAttendants(params = {}) {
    return this.find({
      url: 'desk/analytics/reports/attendants',
      ...params,
      crossDomain: true,
    });
  }

  getReportsTeams(params = {}) {
    return this.find({
      url: 'desk/analytics/reports/teams',
      ...params,
      crossDomain: true,
    });
  }

  getReportsTags(params = {}) {
    return this.find({
      url: 'desk/analytics/reports/tags',
      ...params,
      crossDomain: true,
    });
  }

  getMonitoringTicketMetrics(params = {}) {
    return this.find({
      url: 'desk/monitoring/ticket-metrics',
      ...params,
      crossDomain: true,
    });
  }

  getMonitoringTickets(params = {}) {
    return this.find({
      url: 'desk/monitoring/tickets',
      ...params,
      crossDomain: true,
    });
  }

  getAttendantStatus(params = {}) {
    return this.find({
      url: 'desk/monitoring/attendant-status-metrics',
      ...params,
      crossDomain: true,
    });
  }

  getOpenTickets(params = {}) {
    return this.find({
      url: 'desk/monitoring/open-tickets',
      ...params,
      crossDomain: true,
    });
  }

  getWaitingTickets(params = {}) {
    return this.find({
      url: 'desk/monitoring/waiting-tickets',
      ...params,
      crossDomain: true,
    });
  }

  getMonitoringAttendants(params = {}) {
    return this.find({
      url: 'desk/monitoring/attendants',
      ...params,
      crossDomain: true,
    });
  }

  getMonitoringTeams(params = {}) {
    return this.find({
      url: 'desk/monitoring/teams',
      ...params,
      crossDomain: true,
    });
  }

  getTagsMetrics(params = {}) {
    return this.find({
      url: 'desk/monitoring/tags-metrics',
      ...params,
      crossDomain: true,
    });
  }
}

export default new BlipService(gateway);
